import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import { 
  Search, RotateCcw, Building2, MapPin, Phone, Mail,
  Calendar, ChevronLeft, ChevronRight, Archive, User
} from 'lucide-react';

export const PitchedLeadsPage = () => {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [leads, setLeads] = useState([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(0);
  const [restoring, setRestoring] = useState({});
  const pageSize = 25;

  const fetchLeads = async (page = 0, searchTerm = '') => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        skip: (page * pageSize).toString(),
        limit: pageSize.toString(),
      });
      if (searchTerm) params.append('search', searchTerm);
      
      const res = await api.get(`/api/pitched-leads?${params}`);
      setLeads(res.data.leads || []);
      setTotal(res.data.total || 0);
      setHasMore(res.data.has_more || false);
      setCurrentPage(page);
    } catch (error) {
      console.error('Error fetching pitched leads:', error);
      toast.error('Failed to load archived leads');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLeads();
  }, []);

  const handleSearch = () => {
    fetchLeads(0, search);
  };

  const handleRestore = async (leadId) => {
    if (!window.confirm('Restore this lead back to active leads?')) return;
    
    setRestoring(prev => ({ ...prev, [leadId]: true }));
    try {
      await api.post(`/api/pitched-leads/${leadId}/restore`);
      toast.success('Lead restored to active leads');
      fetchLeads(currentPage, search);
    } catch (error) {
      toast.error('Failed to restore lead');
    } finally {
      setRestoring(prev => ({ ...prev, [leadId]: false }));
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#0A1628]">Already Pitched</h1>
          <p className="text-gray-500">Archived leads excluded from future NPI imports</p>
        </div>
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <Archive className="w-4 h-4" />
          {total} archived leads
        </div>
      </div>

      {/* Search */}
      <div className="bg-white rounded-2xl border border-gray-200 p-4">
        <div className="flex gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              placeholder="Search by company, name, NPI, or email..."
              className="pl-10 rounded-xl"
            />
          </div>
          <Button onClick={handleSearch} className="bg-[#014DB7] text-white rounded-xl">
            <Search className="w-4 h-4 mr-2" /> Search
          </Button>
        </div>
      </div>

      {/* Results */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-[#014DB7] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : leads.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-200 p-12 text-center">
          <Archive className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-[#0A1628] mb-2">No Archived Leads</h3>
          <p className="text-gray-500">
            {search ? 'No leads match your search criteria' : 'Leads you delete will appear here'}
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50">
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Company</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Contact</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">NPI</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Location</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Archived</th>
                <th className="text-right py-3 px-4 text-sm font-medium text-gray-500">Actions</th>
              </tr>
            </thead>
            <tbody>
              {leads.map((lead) => (
                <tr key={lead.id} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-orange-100 rounded-xl flex items-center justify-center">
                        <Building2 className="w-5 h-5 text-orange-600" />
                      </div>
                      <div>
                        <p className="font-medium text-[#0A1628]">{lead.company || '—'}</p>
                        {lead.taxonomy_description && (
                          <p className="text-xs text-gray-400">{lead.taxonomy_description}</p>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    <div className="space-y-1">
                      {(lead.first_name || lead.last_name) && (
                        <div className="flex items-center gap-2 text-sm">
                          <User className="w-4 h-4 text-gray-400" />
                          <span>{lead.first_name} {lead.last_name}</span>
                        </div>
                      )}
                      {lead.email && (
                        <div className="flex items-center gap-2 text-sm text-gray-500">
                          <Mail className="w-4 h-4 text-gray-400" />
                          <span>{lead.email}</span>
                        </div>
                      )}
                      {lead.phone && (
                        <div className="flex items-center gap-2 text-sm text-gray-500">
                          <Phone className="w-4 h-4 text-gray-400" />
                          <span>{lead.phone}</span>
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    {lead.npi ? (
                      <code className="text-sm bg-gray-100 px-2 py-1 rounded">{lead.npi}</code>
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </td>
                  <td className="py-3 px-4">
                    {(lead.city || lead.state) ? (
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <MapPin className="w-4 h-4 text-gray-400" />
                        <span>{lead.city}, {lead.state}</span>
                      </div>
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </td>
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                      <Calendar className="w-4 h-4 text-gray-400" />
                      <span>{formatDate(lead.archived_at)}</span>
                    </div>
                  </td>
                  <td className="py-3 px-4 text-right">
                    <Button
                      onClick={() => handleRestore(lead.id)}
                      disabled={restoring[lead.id]}
                      variant="outline"
                      size="sm"
                      className="rounded-lg text-[#014DB7] border-[#014DB7]/30 hover:bg-[#014DB7]/5"
                    >
                      {restoring[lead.id] ? (
                        <div className="w-4 h-4 border-2 border-[#014DB7]/30 border-t-[#014DB7] rounded-full animate-spin" />
                      ) : (
                        <>
                          <RotateCcw className="w-4 h-4 mr-1" />
                          Restore
                        </>
                      )}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Pagination */}
          {total > pageSize && (
            <div className="p-4 border-t border-gray-200 bg-gray-50 flex items-center justify-between">
              <Button
                onClick={() => fetchLeads(currentPage - 1, search)}
                disabled={currentPage === 0}
                variant="outline"
                className="rounded-xl"
              >
                <ChevronLeft className="w-4 h-4 mr-1" /> Previous
              </Button>
              <span className="text-sm text-gray-500">
                Page {currentPage + 1} of {Math.ceil(total / pageSize)}
              </span>
              <Button
                onClick={() => fetchLeads(currentPage + 1, search)}
                disabled={!hasMore}
                variant="outline"
                className="rounded-xl"
              >
                Next <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PitchedLeadsPage;
